import React, { useMemo, useState, useRef } from "react";
import { useGlobalContext } from "../Config";
import * as XLSX from "xlsx";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";

const ConsumoDomicilio = () => {
  const { baseUrl } = useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);

  // autocomplete
  // seleccionado: { codigo, descripcion }
  const [domicilioValue, setDomicilioValue] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  // resultados
  const [historial, setHistorial] = useState([]);
  const [meta, setMeta] = useState(null); // { codigo, descripcion }

  // debounce
  const timerRef = useRef(null);

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: "include",
      redirect: "follow",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.respuesta === false) {
      const msg = data?.mensaje || `Error HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  const fmtMoneyCLP = (v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return "-";
    return n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  };

  const buscarSuggestions = (query) => {
    const q = String(query || "").trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const url =
          `${baseUrl}domicilio/autocomplete` + `?q=${encodeURIComponent(q)}`;

        const data = await fetchJson(url, { method: "GET" });
        setSuggestions(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        console.warn("Autocomplete error:", e?.message || e);
        setSuggestions([]);
      }
    }, 300);
  };

  const cargarHistorial = async (dom) => {
    const codigo =
      dom?.codigo ??
      dom?.codigoDomicilio ??
      dom?.value?.codigo ??
      dom?.value?.codigoDomicilio ??
      null;

    if (!codigo) {
      console.log("DEBUG domicilio seleccionado:", dom);
      alert("Selecciona un domicilio válido.");
      return;
    }

    setIsLoading(true);
    try {
      const url =
        `${baseUrl}consumoDomicilio/historial` +
        `?codigoDomicilio=${encodeURIComponent(codigo)}`;

      const data = await fetchJson(url, { method: "GET" });

      setMeta({
        codigo: data?.codigoDomicilio ?? codigo,
        descripcion: data?.domicilio ?? dom?.descripcion ?? "",
      });

      const list = Array.isArray(data?.items) ? data.items : [];
      list.sort((a, b) =>
        String(a?.periodo || "").localeCompare(String(b?.periodo || "")),
      );

      setHistorial(list);
    } catch (e) {
      console.error(e);
      alert("Error cargando historial: " + (e?.message || e));
      setMeta(null);
      setHistorial([]);
    } finally {
      setIsLoading(false);
    }
  };

  const limpiar = () => {
    setDomicilioValue(null);
    setSuggestions([]);
    setMeta(null);
    setHistorial([]);
  };

  const headerCard = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <div>
          <h4 style={{ margin: 0, color: "#495057" }}>Consumo Domicilio</h4>
          <small style={{ color: "#6c757d" }}>
            Busca un domicilio y revisa su historial de consumo (todos los
            periodos)
          </small>
        </div>
      </div>
    ),
    [],
  );

  const exportarExcel = () => {
    if (!historial.length) {
      alert("No hay datos para exportar.");
      return;
    }

    const rows = historial.map((row) => ({
      Periodo: row?.periodo ?? "",
      Servicio: row?.tipoServicio ?? "",
      Unidad: row?.unidad ?? "",
      Remarcador: row?.remarcadorId ?? "",
      LecturaAnterior: row?.lecturaMesAnterior ?? "",
      LecturaActual: row?.lecturaMesActual ?? "",
      Consumo: row?.consumoMesActual ?? "",
      ValorUnitario: Number(row?.valorUnitario ?? 0),
      Monto: Number(row?.montoFinal ?? 0),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    ws["!cols"] = [
      { wch: 14 },
      { wch: 18 },
      { wch: 12 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ConsumoDomicilio");

    const codigo = meta?.codigo ?? "sin-codigo";
    XLSX.writeFile(wb, `consumo_domicilio_${codigo}.xlsx`);
  };

  const tablaProps = {
    responsiveLayout: "scroll",
    className: "p-datatable-sm",
    paginator: true,
    rows: 12,
    rowsPerPageOptions: [10, 20, 50],
    stripedRows: true,
    showGridlines: true,
    sortMode: "multiple",
  };

  return (
    <div className="col-12 md:col-12">
      <div
        style={{
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
          marginBottom: "20px",
        }}
      >
        {headerCard}
      </div>

      {/* filtros */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-10">
            <label style={{ fontWeight: "bold" }}>Domicilio</label>
            <AutoComplete
              value={domicilioValue}
              suggestions={suggestions}
              completeMethod={(e) => buscarSuggestions(e.query)}
              field="descripcion"
              dropdown
              forceSelection
              placeholder="Escribe el nombre de un domicilio y selecciona el preferente"
              style={{ width: "100%" }}
              inputStyle={{ width: "100%" }}
              onChange={(e) => setDomicilioValue(e.value)}
              onSelect={(e) => cargarHistorial(e?.value ?? e)}
              disabled={isLoading}
            />
            <small style={{ color: "#6c757d" }}>
              Selecciona un item del listado para cargar el historial.
            </small>
          </div>

          <div className="col-12 md:col-2">
            <label style={{ fontWeight: "bold", opacity: 0 }}>Acciones</label>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                height: "42px",
                marginTop: 4,
              }}
            >
              <Button
                label="Limpiar"
                icon="pi pi-times"
                className="p-button-text"
                onClick={limpiar}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <ProgressSpinner />
        </div>
      )}

      {/* resultados */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            marginBottom: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong>Resultado:</strong>{" "}
            {meta?.descripcion ? (
              <>
                {meta.descripcion}{" "}
                <span style={{ color: "#6c757d" }}>({meta.codigo})</span>
              </>
            ) : (
              "Sin selección"
            )}
            {"  "}
            <span style={{ color: "#6c757d" }}>
              ({historial.length} registros)
            </span>
          </div>

          <Button
            label="Descargar Excel"
            icon="pi pi-file-excel"
            className="p-button-success p-button-sm"
            onClick={exportarExcel}
            disabled={!historial.length || isLoading}
          />
        </div>

        <DataTable
          value={historial}
          dataKey={(row) =>
            `${row?.periodo ?? "NA"}|${row?.tipoServicio ?? "NA"}|${
              row?.remarcadorId ?? "NA"
            }`
          }
          {...tablaProps}
        >
          <Column field="periodo" header="Periodo" sortable />
          <Column field="tipoServicio" header="Servicio" sortable />
          <Column field="cliente" header="Cliente" sortable />
          <Column field="unidad" header="Unidad" />

          <Column field="remarcadorId" header="Remarcador" sortable />
          <Column field="lecturaMesAnterior" header="Lect. ant." sortable />
          <Column field="lecturaMesActual" header="Lect. act." sortable />
          <Column field="consumoMesActual" header="Consumo" sortable />

          <Column
            header="Valor unit."
            body={(row) => `$ ${fmtMoneyCLP(row?.valorUnitario)}`}
            sortable
            sortField="valorUnitario"
          />
          <Column
            header="Monto"
            body={(row) => `$ ${fmtMoneyCLP(row?.montoFinal)}`}
            sortable
            sortField="montoFinal"
          />
        </DataTable>
      </div>
    </div>
  );
};

export default ConsumoDomicilio;
